import { swSettings } from "app/common/services/swSettings";
import BoxSubtitle from "pro-features/components/BoxSubtitle/src/BoxSubtitle";
import BoxTitle from "pro-features/components/BoxTitle/src/BoxTitle";
import SWReactTableWrapper from "app/components/React/Table/SWReactTableWrapper";
import { i18nFilter } from "app/filters/i18nFilter";
import _ from "lodash";
import { StyledHeaderTitle } from "pro-features/pages/conversion/components/benchmarkOvertime/StyledComponents";
import { BoxContainer } from "pro-features/pages/conversion/components/ConversionScatterChart/StyledComponents";
import { ConversionSegmentOSSTableTop } from "app/pages/conversion/oss/ConversionSegmentOSSTableTop/ConversionSegmentOSSTableTop";
import { conversionSegmentsOssTableConfigGen } from "app/pages/conversion/oss/ConversionSegmentsOssTableColumnGenerator";
import type { FunctionComponent } from "react";
import { useState } from "react";
import type { SegementOSSTableData } from "app/services/conversion/conversionApiService";
import ConversionApiService, { CONVERSION_OSS_API } from "app/services/conversion/conversionApiService";
import CountryService from "app/services/CountryService";
import DurationService from "app/services/DurationService";
import StyledBoxSubtitle from "pro-features/styled components/StyledBoxSubtitle/src/StyledBoxSubtitle";
import { ExcelButton } from "app/components/React/ExcelButton/ExcelButton";
import {
    OssEmptyStateContainer,
    FlexContainerCentered,
    PrimaryText,
    SecondaryText,
    StyledLink,
    StyledTitleContainer,
    StyledTitleTextContainer,
    NoDataLabIcon,
} from "./ConversionSegmentOSSStyles";
import type {
    IConversionSegmentOSS,
    IOssTableSelectedFilters,
    IOssTableSelectedWordCount,
} from "./ConversionSegmentOSSTypes";
import { proNavigator } from "single-spa/react-app/ProNavigator";

const apiService = new ConversionApiService();

export const WebsiteSegmentEmptyState = (props) => {
    const { primaryText, secondaryText, children } = props;
    return (
        <OssEmptyStateContainer>
            <FlexContainerCentered>
                <NoDataLabIcon iconName="no-data-lab" />
            </FlexContainerCentered>
            <PrimaryText>{i18nFilter()(primaryText)}</PrimaryText>
            {secondaryText ? <SecondaryText>{i18nFilter()(secondaryText)}</SecondaryText> : null}
            {children}
        </OssEmptyStateContainer>
    );
};

export const ConversionSegmentOSS: FunctionComponent<IConversionSegmentOSS> = (props) => {
    const { pageFilters, ossSupportedCountries } = props;
    const { duration, tab } = proNavigator.getParams();
    const durationData = DurationService.getDurationData(
        duration,
        pageFilters.comparedDuration,
        swSettings.current.componentId,
    );

    const initialTableParams = {
        ...pageFilters,
        comparedDuration: undefined,
        compareFrom: durationData.forAPI.compareFrom,
        compareTo: durationData.forAPI.compareTo,
        pageSize: 100,
    };
    const subtitleFilters = [
        {
            filter: "date",
            value: {
                from: pageFilters.from,
                to: pageFilters.to,
            },
        },
        {
            filter: "webSource",
            value: "Desktop",
        },
    ];
    const [sortByCol, setSortByCol] = useState({ sortbyField: "Upv", sortDirection: "desc" });
    const [tableColumns, setTableColumns] = useState(
        conversionSegmentsOssTableConfigGen(sortByCol, pageFilters.comparedDuration),
    );
    const [availableWordCount, setAvailableWordCount] = useState([]);
    const [ossTableSelectedFilters, setOssTableSelectedFilters] = useState<IOssTableSelectedFilters>();
    const [ossTableSelectedWordCount, setOssTableSelectedWordCount] = useState<IOssTableSelectedWordCount>();

    const updateOssTableFilters = (selectedFilters?: IOssTableSelectedFilters) => {
        setOssTableSelectedFilters(selectedFilters);
    };

    const updateOssTableWordCount = (wordsCount?: IOssTableSelectedWordCount) => {
        setOssTableSelectedWordCount(wordsCount);
    };

    const isHighConfidence = (num) => {
        if (num) {
            return num > 0 && num < 1;
        }
        return false;
    };

    const transformData = (data: SegementOSSTableData) => {
        if (pageFilters.comparedDuration) {
            return {
                ...data,
                Data: data.Data.map((datapoint) => {
                    const {
                        ConfidenceLevel,
                        CompareConfidenceLevel,
                        Cv,
                        CvCompare,
                        Cvr,
                        CvDelta,
                        CvrDelta,
                        CvrCompare,
                    } = datapoint;
                    return {
                        ...datapoint,
                        Cv: isHighConfidence(ConfidenceLevel) ? Cv : null,
                        CvCompare: isHighConfidence(CompareConfidenceLevel) ? CvCompare : null,
                        CvDelta:
                            isHighConfidence(ConfidenceLevel) && isHighConfidence(CompareConfidenceLevel)
                                ? CvDelta
                                : null,
                        Cvr: isHighConfidence(ConfidenceLevel) ? Cvr : null,
                        CvrCompare: isHighConfidence(CompareConfidenceLevel) ? CvrCompare : null,
                        CvrDelta:
                            isHighConfidence(ConfidenceLevel) && isHighConfidence(CompareConfidenceLevel)
                                ? CvrDelta
                                : null,
                    };
                }),
                TotalCount: data.Header.TotalCount,
            };
        }
        return {
            ...data,
            Data: data.Data.map((datapoint) => {
                const { ConfidenceLevel, Cv, CvGrowth, CvrGrowth, Cvr } = datapoint;
                return {
                    ...datapoint,
                    Cv: isHighConfidence(ConfidenceLevel) ? Cv : null,
                    CvGrowth: isHighConfidence(ConfidenceLevel) ? CvGrowth : null,
                    Cvr: isHighConfidence(ConfidenceLevel) ? Cvr : null,
                    CvrGrowth: isHighConfidence(ConfidenceLevel) ? CvrGrowth : null,
                };
            }),
            TotalCount: data.Header.TotalCount,
        };
    };
    const onGetData = (data: SegementOSSTableData) => {
        setAvailableWordCount(_.get(data, "[Filters][WordCount]", []));
    };

    const onSort = ({ field, sortDirection }) => {
        setSortByCol({ sortbyField: field, sortDirection });
    };
    const renderEmptyState = () => {
        return (
            <WebsiteSegmentEmptyState primaryText={"conversion.analysis.oss.invalid.country.title"}>
                <FlexContainerCentered>
                    <SecondaryText>{i18nFilter()("conversion.analysis.oss.invalid.country.subtitle")}</SecondaryText>
                    {ossSupportedCountries.map((country, index) => {
                        if (ossSupportedCountries.length > 1 && index === ossSupportedCountries.length - 1) {
                            return (
                                <>
                                    <SecondaryText>
                                        &nbsp;
                                        {i18nFilter()("conversion.analysis.oss.invalid.country.subtitle.or")}
                                    </SecondaryText>
                                    <StyledLink
                                        key={country}
                                        href={proNavigator.href("conversion-customsegement", {
                                            ...pageFilters,
                                            country,
                                            tab,
                                        })}
                                    >
                                        {CountryService.getCountryById(country).text}
                                    </StyledLink>
                                </>
                            );
                        } else {
                            return (
                                <>
                                    {index > 0 && index <= ossSupportedCountries.length - 2 ? (
                                        <SecondaryText>,</SecondaryText>
                                    ) : null}
                                    <StyledLink
                                        key={country}
                                        href={proNavigator.href("conversion-customsegement", {
                                            ...pageFilters,
                                            country,
                                            tab,
                                        })}
                                    >
                                        {CountryService.getCountryById(country).text}
                                    </StyledLink>
                                </>
                            );
                        }
                    })}
                </FlexContainerCentered>
            </WebsiteSegmentEmptyState>
        );
    };

    const getExcelUrlParams = () => {
        const {
            pageFilters: { sid, country, to, from, isWindow },
        } = props;

        const {
            forAPI: { compareFrom, compareTo },
        } = durationData;

        const { includeTerms = "", excludeTerms = "" } = ossTableSelectedFilters || {};

        const { value: wordCount = undefined } = ossTableSelectedWordCount || {};

        const params = {
            sid,
            country,
            to,
            from,
            isWindow,
            compareFrom,
            compareTo,
            includeTerms,
            excludeTerms,
            wordCount,
        };

        return params;
    };

    const generateExcelDownloadLink = () => {
        const urlParams = getExcelUrlParams();
        const excelUrl = apiService.getOssExcelUrl(urlParams);
        return excelUrl;
    };

    return (
        <BoxContainer data-automation-sites-vs-category={true}>
            <StyledTitleContainer>
                <StyledTitleTextContainer>
                    <StyledHeaderTitle>
                        <BoxTitle tooltip={i18nFilter()("conversion.segment.oss.title.tooltip")}>
                            {i18nFilter()("conversion.segment.oss.title")}
                        </BoxTitle>
                    </StyledHeaderTitle>
                    <StyledBoxSubtitle>
                        <BoxSubtitle filters={subtitleFilters} />
                    </StyledBoxSubtitle>
                </StyledTitleTextContainer>
                <ExcelButton
                    url={generateExcelDownloadLink()}
                    trackName="category oss"
                />
            </StyledTitleContainer>
            {ossSupportedCountries.includes(pageFilters.country) ? (
                <SWReactTableWrapper
                    serverApi={CONVERSION_OSS_API}
                    initialFilters={initialTableParams}
                    tableColumns={tableColumns}
                    tableOptions={{ metric: "ConversionSegmentOSSTable" }}
                    transformData={transformData}
                    getDataCallback={onGetData}
                    recordsField="Data"
                    totalRecordsField="TotalCount"
                    onSort={onSort}
                    pageIndent={0}
                    rowsPerPage={100}
                >
                    {(topComponentProps) => (
                        <ConversionSegmentOSSTableTop
                            availableWordCount={availableWordCount}
                            selectedFilters={ossTableSelectedFilters}
                            selectedWordCount={ossTableSelectedWordCount}
                            updateSelectedFilters={updateOssTableFilters}
                            updateSelectedWordCount={updateOssTableWordCount}
                            {...topComponentProps}
                        />
                    )}
                </SWReactTableWrapper>
            ) : (
                renderEmptyState()
            )}
        </BoxContainer>
    );
};
